import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  Badge,
  Spinner,
  Text,
  HStack,
  VStack,
  useToast,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Checkbox,
  Divider,
} from "@chakra-ui/react";
import { useFindFirst, useFindMany, useGlobalAction } from "@gadgetinc/react";
import { api } from "../../api";

const EvaluationTemplateManager = ({ institution, onUpdate }) => {
  const toast = useToast();
  const [enabledTemplates, setEnabledTemplates] = useState(new Set());
  const [updatingTemplates, setUpdatingTemplates] = useState(new Set());
  const [templateCohorts, setTemplateCohorts] = useState({});
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedCohorts, setSelectedCohorts] = useState([]);

  // Fetch institution configuration
  const [{ data: institutionConfig, fetching: fetchingConfig, error: configError }] = useFindFirst(
    api.institutionConfiguration,
    {
      filter: { institution: { equals: institution } },
      select: {
        id: true,
        institution: true,
        evaluationFormTypes: true,
      },
    }
  );

  // Fetch cohorts for this institution
  const [{ data: cohorts }] = useFindMany(api.cohort, {
    filter: {
      institution: { equals: institution },
      isActive: { equals: true }
    },
    select: {
      id: true,
      name: true,
      displayName: true
    }
  });

  // State for templates
  const [templates, setTemplates] = useState([]);
  const [fetchingTemplates, setFetchingTemplates] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);

  // Fetch evaluation templates using global action
  const [{ data: templatesResult, fetching: loadingTemplates, error: templatesActionError }, getTemplates] = useGlobalAction(
    api.getAvailableEvaluationTemplates
  );

  // Load templates when component mounts or institution changes
  useEffect(() => {
    const loadTemplates = async () => {
      setFetchingTemplates(true);
      setTemplatesError(null);
      try {
        await getTemplates({
          institution,  // CRITICAL: Pass the institution parameter
          trainingLevel: null,
          userId: null,
        });
      } catch (error) {
        setTemplatesError(error);
      } finally {
        setFetchingTemplates(false);
      }
    };

    if (institution) {
      loadTemplates();
    }
  }, [institution]);

  // Update templates state when result is received
  useEffect(() => {
    if (templatesResult?.success && templatesResult?.templates) {
      setTemplates(Array.isArray(templatesResult.templates) ? templatesResult.templates : []);
    } else if (templatesResult?.errors?.length > 0) {
      setTemplatesError(new Error(templatesResult.errors[0].message));
    }
  }, [templatesResult]);

  // Ensure default templates are enabled on mount
  useEffect(() => {
    const ensureDefaultTemplates = async () => {
      if (institution) {
        try {
          const result = await ensureDefaults({ institution });
          if (result?.templatesAdded?.length > 0) {
            console.log('Added default templates:', result.templatesAdded);
            // Reload templates to show the newly enabled ones
            await getTemplates({
              institution,
              trainingLevel: null,
              userId: null,
            });
          }
        } catch (error) {
          console.error('Failed to ensure default templates:', error);
        }
      }
    };
    
    ensureDefaultTemplates();
  }, [institution]);

  // Global action to enable/disable templates
  const [{ fetching: enablingTemplate }, enableTemplate] = useGlobalAction(
    api.enableEvaluationTemplateForInstitution
  );

  const [{ fetching: ensuringDefaults }, ensureDefaults] = useGlobalAction(
    api.ensureDefaultEvaluationTemplates
  );

  // Initialize enabled templates from institution config
  useEffect(() => {
    if (institutionConfig?.evaluationFormTypes) {
      try {
        const enabledIds = Array.isArray(institutionConfig.evaluationFormTypes)
          ? institutionConfig.evaluationFormTypes
          : JSON.parse(institutionConfig.evaluationFormTypes || "[]");
        setEnabledTemplates(new Set(enabledIds));
      } catch (error) {
        console.error("Error parsing evaluation form types:", error);
        setEnabledTemplates(new Set());
      }
    }
  }, [institutionConfig]);

  const handleToggle = async (templateId, templateName, isCurrentlyEnabled) => {
    const updatingSet = new Set(updatingTemplates);
    updatingSet.add(templateId);
    setUpdatingTemplates(updatingSet);

    // Optimistic update
    const newEnabledTemplates = new Set(enabledTemplates);
    if (isCurrentlyEnabled) {
      newEnabledTemplates.delete(templateId);
    } else {
      newEnabledTemplates.add(templateId);
    }
    setEnabledTemplates(newEnabledTemplates);

    try {
      await enableTemplate({
        institution,
        templateId,
        enabled: !isCurrentlyEnabled,
      });

      toast({
        title: "Success",
        description: `${templateName} ${!isCurrentlyEnabled ? "enabled" : "disabled"} for ${institution}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      // Revert optimistic update on error
      setEnabledTemplates(enabledTemplates);

      toast({
        title: "Error",
        description: `Failed to update template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      const updatingSetAfter = new Set(updatingTemplates);
      updatingSetAfter.delete(templateId);
      setUpdatingTemplates(updatingSetAfter);
    }
  };



  if (fetchingConfig || fetchingTemplates) {
    return (
      <Card>
        <CardBody>
          <HStack justify="center" py={8}>
            <Spinner size="lg" />
            <Text>Loading templates...</Text>
          </HStack>
        </CardBody>
      </Card>
    );
  }

  if (configError || templatesError) {
    return (
      <Card>
        <CardBody>
          <Text color="red.500">
            Error loading data: {configError?.message || templatesError?.message}
          </Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <Heading size="md">Evaluation Template Access - {institution}</Heading>
        </CardHeader>
      <CardBody>
        {!templates || templates.length === 0 ? (
          <Text color="gray.500">No evaluation templates available</Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Template Name</Th>
                  <Th>Description</Th>
                  <Th>Available to Cohorts</Th>
                  <Th>Enabled</Th>
                </Tr>
              </Thead>
              <Tbody>
                {templates.map((template) => {
                  const isEnabled = enabledTemplates.has(template.key);
                  const isUpdating = updatingTemplates.has(template.key);

                  return (
                    <Tr key={template.key} opacity={isEnabled ? 1 : 0.6}>
                      <Td fontWeight={isEnabled ? "semibold" : "normal"}>
                        {template.displayName}
                      </Td>
                      <Td>
                        <Text fontSize="sm" noOfLines={2}>
                          {template.description || "—"}
                        </Text>
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingTemplate(template.key);
                            const currentAccess = templateCohorts[template.key];
                            if (currentAccess === 'all' || !currentAccess) {
                              setSelectedCohorts([]);
                            } else {
                              setSelectedCohorts(currentAccess);
                            }
                          }}
                        >
                          {(!templateCohorts[template.key] || templateCohorts[template.key] === 'all') ? (
                            <Badge colorScheme="green">All Cohorts</Badge>
                          ) : (
                            <HStack spacing={1} flexWrap="wrap">
                              {templateCohorts[template.key].slice(0, 2).map(cohortId => {
                                const cohort = cohorts?.find(c => c.id === cohortId);
                                return cohort ? (
                                  <Badge key={cohortId} size="sm" colorScheme="blue">
                                    {cohort.displayName || cohort.name}
                                  </Badge>
                                ) : null;
                              })}
                              {templateCohorts[template.key].length > 2 && (
                                <Badge size="sm" colorScheme="gray">
                                  +{templateCohorts[template.key].length - 2} more
                                </Badge>
                              )}
                            </HStack>
                          )}
                        </Button>
                      </Td>
                      <Td>
                        <HStack>
                          <Switch
                            isChecked={isEnabled}
                            isDisabled={isUpdating}
                            onChange={() =>
                              handleToggle(template.key, template.displayName, isEnabled)
                            }
                            colorScheme="green"
                          />
                          {isUpdating && <Spinner size="sm" />}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </CardBody>
      </Card>

      <Modal isOpen={editingTemplate !== null} onClose={() => setEditingTemplate(null)}>
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Select Cohorts for Template Access</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Checkbox
              isChecked={selectedCohorts.length === 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedCohorts([]);
                }
              }}
            >
              <Text fontWeight="bold">All Cohorts (Default)</Text>
              <Text fontSize="sm" color="gray.400">Template will be available to all current and future cohorts</Text>
            </Checkbox>
            
            <Divider />
            
            <Text fontSize="sm" color="gray.400">Or select specific cohorts:</Text>
            
            <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
              {cohorts?.map(cohort => (
                <Checkbox
                  key={cohort.id}
                  isChecked={selectedCohorts.includes(cohort.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCohorts([...selectedCohorts, cohort.id]);
                    } else {
                      setSelectedCohorts(selectedCohorts.filter(id => id !== cohort.id));
                    }
                  }}
                >
                  {cohort.displayName || cohort.name}
                </Checkbox>
              ))}
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={() => setEditingTemplate(null)}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => {
              setTemplateCohorts({
                ...templateCohorts,
                [editingTemplate]: selectedCohorts.length === 0 ? 'all' : selectedCohorts
              });
              // TODO: Save to backend
              setEditingTemplate(null);
              toast({
                title: 'Cohort access updated',
                description: 'Template cohort access has been updated',
                status: 'success',
                duration: 3000
              });
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
};

export default EvaluationTemplateManager;